import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { engine } from '@/lib/motion/engine'
import { Bracket } from '@/components/landing/primitives'
import { chapters } from '@/constants/content'

import {
    openRequest,
    setAppState,
    useAppState
} from '@/lib/app-store'

const MenuOverlay: FC = (): ReactNode => {
    const open = useAppState(state => state.menuOpen)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setAppState({ menuOpen: false })
        }

        window.addEventListener('keydown', onKey)

        const first = panelRef.current?.querySelector<HTMLElement>('a[href]')
        const id = window.setTimeout(() => first?.focus({ preventScroll: true }), 250)

        return () => {
            window.removeEventListener('keydown', onKey)
            window.clearTimeout(id)
        }
    }, [open])

    const go = (id: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault()
        setAppState({ menuOpen: false })

        const target = document.getElementById(id)

        if (target) engine.scrollTo(target, { duration: 2.6, force: true })
    }

    return (
        <div
            id='site-menu'
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            aria-hidden={!open}
            inert={!open}
            className={cn(
                'fixed inset-0 z-40 bg-[#f6f8f8]/95 backdrop-blur-xl transition-[clip-path] duration-700 ease-[cubic-bezier(0.76,0,0.24,1)]',
                open ? '[clip-path:inset(0_0_0_0)]' : '[clip-path:inset(0_0_100%_0)]'
            )}
        >
            <div className='mx-auto flex h-full max-w-[1600px] flex-col justify-between gap-10 overflow-y-auto px-4 pb-8 pt-24 md:px-10 md:pb-10' data-lenis-prevent=''>
                <nav aria-label='Sections'>
                    <ul className='border-t border-line'>
                        {chapters.map((chapter, index) => (
                            <li key={chapter.id} className='border-b border-line'>
                                <a
                                    href={`#${chapter.id}`}
                                    onClick={go(chapter.id)}
                                    className='group flex items-baseline gap-5 py-2.5 md:gap-8 md:py-3.5'
                                >
                                    <span className='label-mono w-8 shrink-0 text-haze'>
                                        {chapter.index}
                                    </span>

                                    <span
                                        className={cn(
                                            'block text-[clamp(34px,6.2vw,92px)] font-light leading-[0.95] tracking-[-0.055em] text-ink transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-3',
                                            open ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                                        )}
                                        style={{ transitionDelay: open ? `${120 + index * 55}ms` : '0ms' }}
                                    >
                                        {chapter.label}
                                    </span>

                                    <ArrowUpRight
                                        aria-hidden='true'
                                        className='ml-auto size-5 -translate-x-2 text-ink opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100'
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <div className='flex flex-col gap-3'>
                        <Bracket className='text-haze'>Orvane L4 Platform</Bracket>

                        <p className='max-w-sm text-[15px] leading-relaxed text-haze'>
                            Self-flying LiDAR survey for energy, infrastructure, industry and mapping teams.
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={() => openRequest('survey')}
                        className='group inline-flex h-12 items-center gap-3 self-start bg-ink px-6 text-[14px] tracking-[-0.01em] text-white transition-colors duration-300 hover:bg-[#2b3b44] md:self-auto'
                    >
                        Book a Survey Flight
                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </button>
                </div>
            </div>
        </div>
    )
}

export default MenuOverlay
